import React, { FC, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import moment from 'moment-mini';

// components
import Dialog from 'shared/components/Dialog';
import ButtonCross from 'shared/components/Buttons/ButtonCross';
import { StoreItemForm } from '../../StoreForms';

// hooks
import { useCreateStoreItem, useEditStoreItem } from 'hooks/store';

// helpers
import { sendFilesToS3Bucket } from 'core/functions';
import { prepareImages } from 'shared/components/SortableImagesListField';

// validation
import { storeItemValidationSchema } from '../../helpers';

// types
import { IStoreItemResponse, IUpdateStoreItemRequest } from '@joc/api-gateway';
import { StoreItemFormType, StoreItemResponseType } from '../../../types';

import styles from './StoreItemPopup.module.scss';
import qs from 'qs';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

type StoreItemPopupProps = {
	storeItem?: StoreItemResponseType;
	open?: boolean;
	onClose?: () => void;
};

const StoreItemPopup: FC<StoreItemPopupProps> = ({ storeItem, open = true, onClose }) => {
	const location = useLocation();
	const history = useHistory();
	const { createStoreItem } = useCreateStoreItem();
	const { editStoreItem } = useEditStoreItem();
	const [isSaving, setIsSaving] = useState(false);

	const initialValues: StoreItemFormType = useMemo(() => {
		const expirationDateInfo = {
			endDate: storeItem?.expirationDate || undefined,
			endTime: storeItem?.expirationDate ? moment(storeItem.expirationDate).format('HH:mm') : undefined,
		};

		if (storeItem)
			return {
				...storeItem,
				mediaPathes: prepareImages(storeItem.mediaPathes),
				...expirationDateInfo,
			} as unknown as StoreItemFormType;

		return {
			title: '',
			subtitle: '',
			description: '',
			productUrl: '',
			mediaPathes: [],
			price: null,
			countryIds: [],
			...expirationDateInfo,
		};
	}, [storeItem]);

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);

		if (queryParams.has('item')) {
			queryParams.delete('item');

			history.replace({
				search: queryParams.toString(),
			});
		}
	}, [location.search]);

	const handleSubmitClickHandler = async (values: StoreItemFormType) => {
		setIsSaving(true);
		const { mediaPathes, ...body } = values;

		const uploadedImages = await Promise.all(
			mediaPathes.map(async image => {
				if (image.file) {
					const [imageUrl] = await sendFilesToS3Bucket([image.file]);

					return imageUrl;
				}

				return Promise.resolve(image.src);
			})
		);

		const getExpirationDate = (date?: Date, time?: string) => {
			if (!date) return null;

			const selectedDate = moment(date).startOf('day');
			const now = moment();
			const selectedTime = time ? moment(time, 'HH:mm') : null;

			if (selectedDate.isSame(now, 'day')) {
				if (selectedTime && selectedTime.isBefore(now, 'minute')) {
					return now.toDate();
				}
			}

			return selectedTime
				? selectedDate.set({ hours: selectedTime.hours(), minutes: selectedTime.minutes() }).toDate()
				: selectedDate.toDate();
		};

		if (storeItem) {
			editStoreItem({
				id: storeItem.id,
				storeItem: {
					...body,
					price: Number(values.price),
					expirationDate: getExpirationDate(values.endDate, values.endTime),
					mediaPathes: uploadedImages,
				} as unknown as IUpdateStoreItemRequest,
			});
		} else {
			createStoreItem({
				...body,
				price: Number(values?.price),
				expirationDate: getExpirationDate(values.endDate, values.endTime),
				mediaPathes: uploadedImages,
			} as unknown as IStoreItemResponse);
		}

		closePopupClickHandler();
		setIsSaving(false);
	};

	const closePopupClickHandler = () => onClose?.();

	return (
		<Dialog open={open} maxWidth="xl" isFullScreen={true}>
			<section className={styles.storeItemPopup}>
				<span className={styles.storeItemPopup__title}>{storeItem ? 'Edit' : 'Add'} item</span>
				<ButtonCross clickHandler={closePopupClickHandler} parentClassName={styles.storeItemPopup__close} />

				<Formik
					initialValues={initialValues}
					validationSchema={storeItemValidationSchema}
					onSubmit={handleSubmitClickHandler}
				>
					<StoreItemForm isNew={!storeItem} isSaving={isSaving} onCancel={closePopupClickHandler} />
				</Formik>
			</section>
		</Dialog>
	);
};
export default StoreItemPopup;
